import { collection, doc, getCountFromServer, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, Timestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { getPlanKey, PLANS, type PlanKey } from "../config/plans";

const pendingDowngradeAttemptAtMs: Record<string, number> = {};

/**
 * Upsert de la cuenta de suscripción asociada a un owner.
 * - Usa `subscription_accounts/{ownerUserId}` como ID de documento.
 * - Normaliza el plan a las claves de `PLANS` mediante `getPlanKey`.
 * - Completa `familyQuota` desde el plan.
 * - Mantiene/crea timestamps para trazabilidad.
 */
export async function upsertAccountSubscription(params: {
  ownerUserId: string;
  plan: string; // puede venir como 'free' | 'familiar' | 'enterprise' | 'family' | 'premium' ... se normaliza
}): Promise<void> {
  const { ownerUserId } = params;
  const planKey: PlanKey = getPlanKey(params.plan);
  const quota = PLANS[planKey]?.familyQuota ?? 0;
  // Read limits from Admin plan document
  let planLimits: Record<string, number> = {};
  try {
    const planDoc = await getDoc(doc(db, 'subscriptionPlans', planKey));
    if (planDoc.exists()) {
      const d = planDoc.data() as any;
      if (d && typeof d.limits === 'object' && d.limits) {
        planLimits = Object.fromEntries(Object.entries(d.limits).map(([k,v]) => [k, Number(v) || 0]));
      }
    }
    // Backward compat: if no explicit families limit, derive from legacy familyQuota
    if (planLimits && planLimits.families == null) planLimits.families = quota;
  } catch {}

  const ref = doc(db, "subscription_accounts", ownerUserId);
  const snap = await getDoc(ref);
  const base = {
    ownerUserId,
    plan: planKey,
    familyQuota: quota,
    limits: planLimits,
    // Si el plan es free, billingStatus puede permanecer 'free' o 'none'
    // Si es pago, por ahora lo dejamos en 'active' (hasta integrar proveedor de cobro)
    billingStatus: planKey === "free" ? "free" : "active",
    updatedAt: serverTimestamp(),
  } as const;

  if (snap.exists()) {
    await setDoc(ref, base, { merge: true });
  } else {
    await setDoc(ref, {
      ...base,
      createdAt: serverTimestamp(),
      familiesCount: 0,
    });
  }
}

/** Transfiere ownership de una familia a otro usuario del mismo grupo (cliente) */
export async function transferFamilyOwnership(params: {
  familyId: string;
  oldOwnerUserId: string;
  newOwnerUserId: string;
}): Promise<{ ok: true } | { ok: false; reason: string }> {
  const { familyId, oldOwnerUserId, newOwnerUserId } = params;
  // Leer familia y validar ownership actual
  const famRef = doc(db, "families", familyId);
  const famSnap = await getDoc(famRef);
  if (!famSnap.exists()) return { ok: false, reason: "Familia no encontrada" };
  const fam = famSnap.data() as any;
  if ((fam.ownerId || null) !== oldOwnerUserId) return { ok: false, reason: "No eres el owner actual" };

  // Verificar que el nuevo owner pertenezca a la familia (users.familyId o users.familyIds contiene familyId)
  const usersCol = collection(db, "users");
  // Intento 1: familyId directo
  let isEligible = false;
  try {
    const q1 = query(usersCol, where("familyId", "==", familyId));
    const c1 = await getCountFromServer(q1);
    if (c1.data().count > 0) {
      // no sabemos si incluye ese uid, hacemos lectura directa del user
      const userDoc = await getDoc(doc(db, "users", newOwnerUserId));
      const d = userDoc.exists() ? (userDoc.data() as any) : null;
      if (d && (d.familyId === familyId || (Array.isArray(d.familyIds) && d.familyIds.includes(familyId)))) isEligible = true;
    }
  } catch {}
  if (!isEligible) {
    try {
      const q2 = query(usersCol, where("familyIds", "array-contains", familyId));
      const c2 = await getCountFromServer(q2);
      if (c2.data().count > 0) {
        const userDoc = await getDoc(doc(db, "users", newOwnerUserId));
        const d = userDoc.exists() ? (userDoc.data() as any) : null;
        if (d && (d.familyId === familyId || (Array.isArray(d.familyIds) && d.familyIds.includes(familyId)))) isEligible = true;
      }
    } catch {}
  }
  if (!isEligible) return { ok: false, reason: "El usuario destino no pertenece a esta familia" };

  // Actualizar familia: ownerId y admins (asegurar que el nuevo owner está en admins)
  const admins: string[] = Array.isArray(fam.admins) ? [...fam.admins] : [];
  if (!admins.includes(newOwnerUserId)) admins.push(newOwnerUserId);
  // Opcional: remover old owner de admins si se desea; de momento lo mantenemos.
  await updateDoc(famRef, { ownerId: newOwnerUserId, admins });

  // Sincronizar contadores para old y new owners
  try { await syncAccountFamiliesCount(oldOwnerUserId); } catch {}
  try { await syncAccountFamiliesCount(newOwnerUserId); } catch {}

  return { ok: true };
}

/** Lee la cuenta de suscripción del owner */
export async function getAccountSubscription(ownerUserId: string): Promise<any | null> {
  const ref = doc(db, "subscription_accounts", ownerUserId);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/** Cuenta familias cuyo ownerId es el ownerUserId */
export async function countOwnerFamilies(ownerUserId: string): Promise<number> {
  try {
    const q = query(collection(db, "families"), where("ownerId", "==", ownerUserId));
    const cnt = await getCountFromServer(q);
    return Number(cnt.data().count || 0);
  } catch (err) {
    return 0;
  }
}

/** Sincroniza familiesCount en subscription_accounts con el número real de familias del owner */
export async function syncAccountFamiliesCount(ownerUserId: string): Promise<number> {
  try {
    const total = await countOwnerFamilies(ownerUserId);
    const ref = doc(db, "subscription_accounts", ownerUserId);
    await setDoc(ref, { ownerUserId, familiesCount: total, updatedAt: serverTimestamp() }, { merge: true });
    return total;
  } catch (err) {
    return 0;
  }
}

/**
 * Procesa un pago exitoso de Mercado Pago y actualiza la suscripción del usuario
 * @param userId - ID del usuario que realizó el pago
 * @param planId - ID del plan ('free' | 'family' | 'enterprise')
 * @param subscriptionType - Tipo de suscripción ('monthly' | 'annual')
 * @param paymentData - Datos adicionales del pago (opcional)
 */
export async function processSuccessfulPayment(
  userId: string,
  planId: 'free' | 'familiar' | 'premium',
  subscriptionType: 'monthly' | 'annual',
  paymentData?: {
    transactionId?: string;
    amount?: number;
    currency?: string;
  }
): Promise<boolean> {
  try {
    const now = new Date();
    const planKey: PlanKey = getPlanKey(planId);
    
    // Calcular fecha de expiración según tipo de suscripción
    const currentPeriodEnd = new Date(now);
    if (subscriptionType === 'monthly') {
      // Mensual: +1 mes desde hoy
      currentPeriodEnd.setMonth(currentPeriodEnd.getMonth() + 1);
    } else {
      // Anual: +12 meses desde hoy
      currentPeriodEnd.setFullYear(currentPeriodEnd.getFullYear() + 1);
    }
    
    console.log(`✅ Procesando pago ${subscriptionType} - Plan: ${planKey} - Expira: ${currentPeriodEnd.toISOString()}`);
    
    // 1. Actualizar subscription_accounts
    await setDoc(doc(db, 'subscription_accounts', userId), {
      ownerUserId: userId,
      plan: planKey,
      billingStatus: 'active',
      subscriptionType: subscriptionType,
      currentPeriodEnd: Timestamp.fromDate(currentPeriodEnd),
      lastPaymentDate: Timestamp.fromDate(now),
      ...(paymentData?.transactionId && { lastTransactionId: paymentData.transactionId }),
      ...(paymentData?.amount && { lastPaymentAmount: paymentData.amount }),
      ...(paymentData?.currency && { currency: paymentData.currency }),
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    // 2. Actualizar users.subscription
    await updateDoc(doc(db, 'users', userId), {
      'subscription.plan': planKey,
      'subscription.status': 'active',
      'subscription.subscriptionType': subscriptionType,
      'subscription.currentPeriodEnd': Timestamp.fromDate(currentPeriodEnd),
      'subscription.startDate': Timestamp.fromDate(now),
      updatedAt: serverTimestamp()
    });
    
    // 3. Sincronizar plan en todas las familias donde es owner
    const familiesQuery = query(
      collection(db, 'families'),
      where('ownerId', '==', userId)
    );
    const familiesSnap = await getCountFromServer(familiesQuery);
    
    if (familiesSnap.data().count > 0) {
      const familiesDocs = await getDocs(familiesQuery);
      for (const familyDoc of familiesDocs.docs) {
        await updateDoc(doc(db, 'families', familyDoc.id), {
          plan: planKey,
          billingStatus: 'active',
          updatedAt: serverTimestamp()
        });
      }
    }
    
    console.log(`✅ Pago procesado exitosamente para usuario ${userId}`);
    return true;
    
  } catch (error) {
    console.error('❌ Error procesando pago:', error);
    return false;
  }
}

/**
 * Activa el trial de 14 días para una familia nueva
 * @param familyId - ID de la familia
 * @param ownerId - ID del owner de la familia
 */
export async function activateFamilyTrial(familyId: string, ownerId: string): Promise<boolean> {
  try {
    const now = new Date();
    const trialEnd = new Date(now);
    trialEnd.setDate(trialEnd.getDate() + 14); // 14 días de trial
    
    // Actualizar familia con trial
    await updateDoc(doc(db, 'families', familyId), {
      plan: 'familiar', // Durante el trial tienen acceso al plan familiar
      trialStartAt: Timestamp.fromDate(now),
      trialEndsAt: Timestamp.fromDate(trialEnd),
      billingStatus: 'trial',
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Trial de 14 días activado para familia ${familyId} (expira: ${trialEnd.toISOString()})`);
    return true;
    
  } catch (error) {
    console.error('❌ Error activando trial:', error);
    return false;
  }
}

/** Sincroniza el campo 'plan' en el documento de la familia con el plan del owner */
export async function syncFamilyPlanFromOwner(familyId: string): Promise<boolean> {
  try {
    // Obtener owner de la familia
    const famSnap = await getDoc(doc(db, 'families', familyId));
    if (!famSnap.exists()) {
      return false;
    }
    
    const ownerId = (famSnap.data() as any).ownerId;
    if (!ownerId) {
      return false;
    }
    
    // Obtener plan del owner
    const acc = await getAccountSubscription(ownerId);
    const ownerPlan = acc?.plan || 'free';
    
    // Normalizar plan
    const normalizedPlan: PlanKey = getPlanKey(ownerPlan);
    
    // Actualizar documento de familia
    await updateDoc(doc(db, 'families', familyId), {
      plan: normalizedPlan,
      updatedAt: serverTimestamp()
    });
    
    return true;
    
  } catch (err) {
    return false;
  }
}

/** Verifica si el owner puede crear una nueva familia según su cuota */
export async function canCreateFamily(ownerUserId: string): Promise<{ allowed: boolean; remaining: number; quota: number; current: number; plan: PlanKey }> {
  try {
    // Asegurar que exista cuenta con plan normalizado
    const acc = (await getAccountSubscription(ownerUserId)) || {};
    const plan: PlanKey = getPlanKey(acc.plan || "free");
    // Prefer account limits.families; fallback to plan doc limits or legacy PLANS
    let quota = Number(acc?.limits?.families);
    if (!quota && quota !== 0) {
      try {
        const planDoc = await getDoc(doc(db,'subscriptionPlans', plan));
        if (planDoc.exists()) {
          const d = planDoc.data() as any;
          if (d?.limits && typeof d.limits.families === 'number') quota = d.limits.families;
        }
      } catch {}
    }
    if (!quota && quota !== 0) quota = PLANS[plan]?.familyQuota ?? 0;
    // Contar familias reales para evitar inconsistencias si familiesCount no está actualizado
    const current = await countOwnerFamilies(ownerUserId);
    const remaining = Math.max(0, quota - current);
    return { allowed: remaining > 0, remaining, quota, current, plan }
  } catch (err) {
    // En caso de error, permitir la creación (plan free por defecto)
    return { allowed: true, remaining: 1, quota: 1, current: 0, plan: 'free' };
  }
}

/** 
 * Obtiene los límites del plan para una familia - SIEMPRE lee desde subscriptionPlans (AdminPlans)
 * Verifica automáticamente expiraciones de trial y suscripciones pagadas
 */
export async function getFamilyPlanLimits(familyId: string): Promise<{ families: number; members: number; admins: number; activities: number; checklists: number; rewards: number; familyCalls: number; plan: PlanKey }> {
  try {
    const famSnap = await getDoc(doc(db, 'families', familyId));
    if (!famSnap.exists()) {
      return { families: 0, members: 0, admins: 0, activities: 0, checklists: 0, rewards: -1, familyCalls: -1, plan: 'free' };
    }
    
    const familyData = famSnap.data() as any;
    const ownerId = familyData.ownerId;
    
    if (!ownerId) {
      return { families: 0, members: 0, admins: 0, activities: 0, checklists: 0, rewards: -1, familyCalls: 0, plan: 'free' };
    }
    
    // Obtener plan del owner desde subscription_accounts
    const acc = (await getAccountSubscription(ownerId)) || {};
    let planKey: PlanKey = getPlanKey(acc.plan || "free");
    const now = new Date();

    // ✅ TRIAL DE FAMILIA ACTIVO: durante el trial, comportarse como plan pago (Familiar)
    // Solo aplica si el owner efectivamente está en Free.
    try {
      if (planKey === 'free' && familyData?.trialEndsAt) {
        const trialEndDate = familyData.trialEndsAt?.toDate
          ? familyData.trialEndsAt.toDate()
          : new Date(familyData.trialEndsAt);
        if (trialEndDate && !isNaN(trialEndDate.getTime()) && now < trialEndDate) {
          planKey = 'familiar';
        }
      }
    } catch {
      // ignore
    }
  
  // ⭐ VERIFICACIÓN COMPLETA DE EXPIRACIÓN
  let needsDowngrade = false;
  let expirationReason = '';
  
  try {
    // 0️⃣ Cargar suscripción del owner
    const userDoc = await getDoc(doc(db, 'users', ownerId));
    let subscription: any = null;
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      subscription = userData.subscription;
    }

    // 0️⃣b Si tiene un currentPeriodEnd y ya venció, debe expirar (aunque el status sea "active")
    // Esto evita que una suscripción quede activa indefinidamente por estado desincronizado.
    if (
      subscription?.plan &&
      subscription.plan !== 'free' &&
      subscription?.currentPeriodEnd
    ) {
      const periodEndDate = subscription.currentPeriodEnd.toDate
        ? subscription.currentPeriodEnd.toDate()
        : new Date(subscription.currentPeriodEnd);

      if (now > periodEndDate) {
        needsDowngrade = true;
        expirationReason = 'Suscripción paga expirada';
        console.log('⚠️ Suscripción expirada:', periodEndDate);
      }
    }
    
    // Solo verificar expiraciones si NO tiene suscripción activa
    // Además, el trial de familia solo debe poder degradar si el plan efectivo es FREE.
    if (!needsDowngrade && (!subscription || subscription?.plan === 'free')) {
      // 1️⃣ VERIFICAR TRIAL DE FAMILIA (14 días desde trialStartAt)
      // Este trial se aplica a nivel de familia cuando se crea
      if (familyData.trialEndsAt) {
        const trialEndDate = familyData.trialEndsAt.toDate 
          ? familyData.trialEndsAt.toDate() 
          : new Date(familyData.trialEndsAt);
        
        if (now > trialEndDate && planKey === 'free') {
          // Trial de familia expirado
          needsDowngrade = true;
          expirationReason = 'Trial de 14 días expirado';
          console.log('⚠️ Trial de familia expirado');
        }
      }
      
      // 2️⃣ VERIFICAR SUSCRIPCIÓN PAGA DEL OWNER
      if (!needsDowngrade && subscription) {
        
        // Verificar trial de usuario (legacy)
        if (subscription?.status === 'trial' && subscription?.trialEnd) {
          const trialEndDate = subscription.trialEnd.toDate 
            ? subscription.trialEnd.toDate() 
            : new Date(subscription.trialEnd);
          
          if (now > trialEndDate) {
            needsDowngrade = true;
            expirationReason = 'Trial de usuario expirado';
          }
        }
        
        // Verificar suscripción paga expirada (Mercado Pago)
        if (!needsDowngrade && subscription?.plan && subscription.plan !== 'free') {
          if (subscription?.currentPeriodEnd) {
            const periodEndDate = subscription.currentPeriodEnd.toDate 
              ? subscription.currentPeriodEnd.toDate() 
              : new Date(subscription.currentPeriodEnd);
            
            if (now > periodEndDate) {
              needsDowngrade = true;
              expirationReason = 'Suscripción paga expirada';
              console.log('⚠️ Suscripción expirada:', periodEndDate);
            }
          }
        }
      }
    }
    
    // 3️⃣ MARCAR DOWNGRADE PENDIENTE (el wizard lo aplicará después de confirmación del usuario)
    if (needsDowngrade) {
      planKey = 'free';

      const throttleKey = `${ownerId}`;
      const lastAttempt = pendingDowngradeAttemptAtMs[throttleKey] || 0;
      const nowAttempt = Date.now();
      // Throttle cross-reload (localStorage) + in-memory (fallback)
      try {
        const lsKey = `pending_downgrade_attempt_${ownerId}`;
        const lastLs = Number(localStorage.getItem(lsKey) || '0');
        if (nowAttempt - lastLs < 60_000) {
          return { families: 0, members: 0, admins: 0, activities: 0, checklists: 0, rewards: -1, familyCalls: -1, plan: planKey };
        }
        localStorage.setItem(lsKey, String(nowAttempt));
      } catch {
        // ignore storage errors
      }
      if (nowAttempt - lastAttempt < 60_000) {
        return { families: 0, members: 0, admins: 0, activities: 0, checklists: 0, rewards: -1, familyCalls: -1, plan: planKey };
      }
      pendingDowngradeAttemptAtMs[throttleKey] = nowAttempt;
      
      // ⭐ NUEVO: Guardar pending downgrade para que el wizard lo detecte
      try {
        const { setPendingDowngrade, getPendingDowngrade } = await import('./pendingDowngradeService');
        
        // ⭐ VERIFICAR SI YA EXISTE UN PENDING DOWNGRADE (evitar crear duplicados)
        const existingPending = await getPendingDowngrade(ownerId);
        
        if (existingPending) {
          console.log('ℹ️ Ya existe un pending downgrade, no crear otro');
        } else {
          // Obtener el plan actual del usuario
          const currentPlanId = acc?.plan || 'familiar';
          
          // ⭐ VERIFICAR QUE EL PLAN REALMENTE REQUIERE DOWNGRADE
          // Si el plan actual ya es 'free', no crear pending downgrade
          if (currentPlanId === 'free') {
            console.log('ℹ️ Plan ya es free, no crear pending downgrade');
          } else {
            await setPendingDowngrade(
              ownerId,
              familyId,
              currentPlanId,
              'free',
              'expiration',
              expirationReason
            );
            
            console.log('⏸️ Downgrade pendiente guardado. El usuario debe completar el wizard.');
          }
        }
      } catch (pendingErr) {
        console.error('Error guardando pending downgrade:', pendingErr);
        
        // FALLBACK: Si falla, aplicar downgrade automático (comportamiento anterior)
        try {
          await updateDoc(doc(db, 'subscription_accounts', ownerId), {
            plan: 'free',
            billingStatus: 'expired',
            updatedAt: serverTimestamp()
          });
          
          await updateDoc(doc(db, 'users', ownerId), {
            'subscription.plan': 'free',
            'subscription.status': 'expired',
            updatedAt: serverTimestamp()
          });
          
          await updateDoc(doc(db, 'families', familyId), {
            plan: 'free',
            billingStatus: 'expired',
            updatedAt: serverTimestamp()
          });
        } catch (updateErr) {
          // Ignorar errores de permisos silenciosamente
        }
      }
    }
  } catch (expirationCheckErr) {
    // Silent fail - expiración se verificará en la próxima consulta
  }
  
  // planKey ya coincide con los IDs de documentos en subscriptionPlans
  const planDocName = planKey;
  
  let families = 0, members = 0, admins = 0, activities = 0, checklists = 0, rewards = -1;
  try {
    const planDoc = await getDoc(doc(db, 'subscriptionPlans', planDocName));
    if (planDoc.exists()) {
      const planData = planDoc.data();
      const limits = planData?.limits || {};
      families = Number(limits.families) || 0;
      members = Number(limits.members) || 0;
      admins = Number(limits.admins) || 0;
      activities = Number(limits.activities) || 0;
      checklists = Number(limits.checklists) || 0;
      rewards = Number(limits.rewards) ?? -1; // -1 = bloqueado (plan free), 0 = ilimitado, >0 = límite
      const familyCalls = Number(limits.familyCalls) ?? -1; // -1 = bloqueado, 0 o 999 = ilimitado
      return { families, members, admins, activities, checklists, rewards, familyCalls, plan: planKey };
    }
  } catch (err) {
    // Error reading plan limits - silent fail, will return 0s
  }

    return { families: 0, members: 0, admins: 0, activities: 0, checklists: 0, rewards: -1, familyCalls: -1, plan: 'free' };
    
  } catch (error) {
    // Retornar plan free en caso de error
    return { families: 0, members: 0, admins: 0, activities: 0, checklists: 0, rewards: -1, familyCalls: -1, plan: 'free' };
  }
}

/** Verifica si se puede agregar un miembro */
export async function canAddMember(familyId: string): Promise<{ allowed: boolean; remaining: number; limit: number; current: number }> {
  const limits = await getFamilyPlanLimits(familyId);
  const limit = limits.members;
  if (limit === 0) return { allowed: true, remaining: -1, limit: 0, current: 0 };
  const q = query(collection(db, 'members'), where('familyId', '==', familyId));
  const cnt = await getCountFromServer(q);
  const current = Number(cnt.data().count || 0);
  const remaining = Math.max(0, limit - current);
  return { allowed: remaining > 0, remaining, limit, current };
}

/** Verifica si se puede agregar un admin */
export async function canAddAdmin(familyId: string): Promise<{ allowed: boolean; remaining: number; limit: number; current: number }> {
  const limits = await getFamilyPlanLimits(familyId);
  const limit = limits.admins;
  if (limit === 0) return { allowed: true, remaining: -1, limit: 0, current: 0 };
  const famSnap = await getDoc(doc(db, 'families', familyId));
  if (!famSnap.exists()) return { allowed: false, remaining: 0, limit, current: 0 };
  const admins = Array.isArray((famSnap.data() as any).admins) ? (famSnap.data() as any).admins : [];
  const current = admins.length;
  const remaining = Math.max(0, limit - current);
  return { allowed: remaining > 0, remaining, limit, current };
}

/** Verifica si se puede crear una actividad */
export async function canCreateActivity(familyId: string): Promise<{ allowed: boolean; remaining: number; limit: number; current: number }> {
  const limits = await getFamilyPlanLimits(familyId);
  const limit = limits.activities;
  if (limit === 0) return { allowed: true, remaining: -1, limit: 0, current: 0 };
  const q = query(collection(db, 'activities'), where('familyId', '==', familyId));
  const cnt = await getCountFromServer(q);
  const current = Number(cnt.data().count || 0);
  const remaining = Math.max(0, limit - current);
  return { allowed: remaining > 0, remaining, limit, current };
}

/** Verifica si se puede crear un checklist */
export async function canCreateChecklist(familyId: string): Promise<{ allowed: boolean; remaining: number; limit: number; current: number }> {
  const limits = await getFamilyPlanLimits(familyId);
  const limit = limits.checklists;
  if (limit === 0) return { allowed: true, remaining: -1, limit: 0, current: 0 };
  const q = query(collection(db, 'checklist'), where('familyId', '==', familyId));
  const cnt = await getCountFromServer(q);
  const current = Number(cnt.data().count || 0);
  const remaining = Math.max(0, limit - current);
  return { allowed: remaining > 0, remaining, limit, current };
}

/** Verifica si se puede crear una recompensa */
export async function canCreateReward(familyId: string): Promise<{ allowed: boolean; remaining: number; limit: number; current: number }> {
  const limits = await getFamilyPlanLimits(familyId);
  const limit = limits.rewards;
  // -1 = bloqueado (plan free), 0 = ilimitado, >0 = límite específico
  if (limit === -1) return { allowed: false, remaining: 0, limit: -1, current: 0 };
  if (limit === 0) return { allowed: true, remaining: -1, limit: 0, current: 0 };
  const q = query(collection(db, 'rewards'), where('familyId', '==', familyId));
  const cnt = await getCountFromServer(q);
  const current = Number(cnt.data().count || 0);
  const remaining = Math.max(0, limit - current);
  return { allowed: remaining > 0, remaining, limit, current };
}

/** Verifica si el marketplace está habilitado para una familia */
export async function isMarketplaceEnabled(familyId: string): Promise<boolean> {
  const limits = await getFamilyPlanLimits(familyId);
  // Si rewards es -1, el marketplace está bloqueado
  return limits.rewards !== -1;
}
